import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const LibraryContext = createContext();

export const LibraryProvider = ({ children }) => {
  const [songs, setSongs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all songs from the API
  const fetchSongs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/songs');
      setSongs(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching songs:', err);
      setError('Erreur lors du chargement des morceaux');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all playlists from the API
  const fetchPlaylists = async () => {
    setLoading(true);
    try {
      const res = await api.get('/playlists');
      setPlaylists(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching playlists:', err);
      setError('Erreur lors du chargement des playlists');
    } finally {
      setLoading(false);
    }
  };

  // Upload a song
  const uploadSong = async (formData) => {
    setLoading(true);
    try {
      const res = await api.post('/songs/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setSongs([res.data, ...songs]);
      setError(null);
      return res.data;
    } catch (err) {
      console.error('Error uploading song:', err);
      setError('Erreur lors du téléchargement du morceau');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Download a song from YouTube
  const downloadFromYoutube = async (youtubeData) => {
    setLoading(true);
    try {
      // Utiliser une configuration spécifique pour cette requête
      const res = await api.post('/youtube/download', youtubeData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        withCredentials: true,
        timeout: 300000 // 5 minutes timeout pour les téléchargements longs
      });
      
      setSongs([res.data, ...songs]);
      setError(null);
      return res.data;
    } catch (err) {
      console.error('Error downloading from YouTube:', err);
      setError(`Erreur lors du téléchargement depuis YouTube: ${err.message || 'Erreur réseau'}`);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Create a new playlist
  const createPlaylist = async (playlistData) => {
    setLoading(true);
    try {
      const res = await api.post('/playlists', playlistData);
      setPlaylists([res.data, ...playlists]);
      setError(null);
      return res.data;
    } catch (err) {
      console.error('Error creating playlist:', err);
      setError('Erreur lors de la création de la playlist');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Add a song to a playlist
  const addSongToPlaylist = async (playlistId, songId) => {
    setLoading(true);
    try {
      const res = await api.put(`/playlists/${playlistId}/add-song/${songId}`);
      
      // Update the playlists state
      setPlaylists(playlists.map(playlist => 
        playlist._id === playlistId ? res.data : playlist
      ));
      
      setError(null);
      return res.data;
    } catch (err) {
      console.error('Error adding song to playlist:', err);
      setError('Erreur lors de l\'ajout du morceau à la playlist');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Remove a song from a playlist
  const removeSongFromPlaylist = async (playlistId, songId) => {
    setLoading(true);
    try {
      const res = await api.put(`/playlists/${playlistId}/remove-song/${songId}`);
      
      // Update the playlists state
      setPlaylists(playlists.map(playlist => 
        playlist._id === playlistId ? res.data : playlist
      ));
      
      setError(null);
      return res.data;
    } catch (err) {
      console.error('Error removing song from playlist:', err);
      setError('Erreur lors de la suppression du morceau de la playlist');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete a song
  const deleteSong = async (songId) => {
    setLoading(true);
    try {
      await api.delete(`/songs/${songId}`);
      setSongs(songs.filter(song => song._id !== songId));
      setError(null);
    } catch (err) {
      console.error('Error deleting song:', err);
      setError('Erreur lors de la suppression du morceau');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete a playlist
  const deletePlaylist = async (playlistId) => {
    setLoading(true);
    try {
      await api.delete(`/playlists/${playlistId}`);
      setPlaylists(playlists.filter(playlist => playlist._id !== playlistId));
      setError(null);
    } catch (err) {
      console.error('Error deleting playlist:', err);
      setError('Erreur lors de la suppression de la playlist');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Find similar songs
  const findSimilarSongs = async (songId) => {
    setLoading(true);
    try {
      const res = await api.get(`/analysis/similar/${songId}`);
      setError(null);
      return res.data;
    } catch (err) {
      console.error('Error finding similar songs:', err);
      setError('Erreur lors de la recherche de morceaux similaires');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Generate a smart playlist
  const generateSmartPlaylist = async (songIds, transitionType, sortBy) => {
    setLoading(true);
    try {
      const res = await api.post('/analysis/smart-playlist', {
        songIds,
        transitionType,
        sortBy
      });
      setError(null);
      return res.data;
    } catch (err) {
      console.error('Error generating smart playlist:', err);
      setError('Erreur lors de la génération de la playlist intelligente');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Filter songs by criteria
  const filterSongs = async (criteria) => {
    setLoading(true);
    try {
      const res = await api.get('/songs/filter', { params: criteria });
      setError(null);
      return res.data;
    } catch (err) {
      console.error('Error filtering songs:', err);
      setError('Erreur lors du filtrage des morceaux');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchSongs();
    fetchPlaylists();
  }, []);

  return (
    <LibraryContext.Provider
      value={{
        songs,
        playlists,
        loading,
        error,
        fetchSongs,
        fetchPlaylists,
        uploadSong,
        downloadFromYoutube,
        createPlaylist,
        addSongToPlaylist,
        removeSongFromPlaylist,
        deleteSong,
        deletePlaylist,
        findSimilarSongs,
        generateSmartPlaylist,
        filterSongs
      }}
    >
      {children}
    </LibraryContext.Provider>
  );
};

export const useLibrary = () => useContext(LibraryContext);
